/* ==========================================
   CJM – Product Add-ons (Dawn-proof)
   - Intercepts the Add to cart button click (capture phase)
   - Adds base + selected add-ons in ONE request to /cart/add.js
   - Updates per-card "+ $X" when add-on variant dropdown changes
   ========================================== */

(function () {
  function formatMoneyFromCents(cents) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents);
    return `$${(cents / 100).toFixed(2)}`;
  }

  // ---------------------------
  // "What's this?" modal helpers
  // ---------------------------
  function ensureModal() {
    let overlay = document.querySelector(".cjm-wt-overlay");
    if (overlay) return overlay;

    overlay = document.createElement("div");
    overlay.className = "cjm-wt-overlay";
    overlay.innerHTML = `
      <div class="cjm-wt-modal" role="dialog" aria-modal="true" aria-labelledby="cjm-wt-title">
        <div class="cjm-wt-header">
          <h3 class="cjm-wt-title" id="cjm-wt-title">What's this?</h3>
          <button class="cjm-wt-close" type="button" aria-label="Close">×</button>
        </div>
        <div class="cjm-wt-body"></div>
      </div>
    `;
    document.body.appendChild(overlay);

    function closeModal() {
      overlay.classList.remove("is-open");
    }

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closeModal();
    });
    overlay.querySelector(".cjm-wt-close").addEventListener("click", closeModal);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && overlay.classList.contains("is-open")) closeModal();
    });

    overlay._cjmClose = closeModal;
    return overlay;
  }

  function openModalFromTopic(topic, fallbackTitle) {
    const overlay = ensureModal();
    const titleEl = overlay.querySelector(".cjm-wt-title");
    const bodyEl = overlay.querySelector(".cjm-wt-body");

    titleEl.textContent = topic?.title || fallbackTitle || "What's this?";
    bodyEl.innerHTML = topic?.html || "";

    if (Array.isArray(topic?.images) && topic.images.length) {
      const media = document.createElement("div");
      media.className = "cjm-wt-media";
      topic.images.forEach((src) => {
        if (!src) return;
        const img = document.createElement("img");
        img.loading = "lazy";
        img.src = src;
        img.alt = "";
        media.appendChild(img);
      });
      bodyEl.appendChild(media);
    }

    overlay.classList.add("is-open");
    overlay.querySelector(".cjm-wt-close").focus();
  }

  // ---------------------------
  // Add-ons logic
  // ---------------------------
  function initAddons(scope) {
    const root = scope || document;

    const container = root.querySelector("[data-cjm-addons]");
    if (!container) return;

    const plusEl = container.querySelector("[data-cjm-addons-plus]");
    const cards = Array.from(container.querySelectorAll("[data-addon-card]"));

    function getSelectedAddons() {
      const items = [];
      let cents = 0;

      cards.forEach((card) => {
        const check = card.querySelector("input[data-addon-variant-id]");
        if (!check || !check.checked) return;

        const select = card.querySelector("select[data-addon-select]");
        if (select) {
          const opt = select.options[select.selectedIndex];
          const id = parseInt(opt.value, 10);
          const price = parseInt(opt.dataset.priceCents || "0", 10);
          if (id) items.push({ id, quantity: 1 });
          cents += price;
        } else {
          const id = parseInt(check.dataset.addonVariantId, 10);
          const price = parseInt(check.dataset.addonPriceCents || "0", 10);
          if (id) items.push({ id, quantity: 1 });
          cents += price;
        }
      });

      return { items, cents };
    }

    // Update the "+ $X" on the right side of each card to reflect the currently selected variant price
    function updateCardPrice(card) {
      const priceEl = card.querySelector(".cjm-addon-card__price");
      if (!priceEl) return;

      const select = card.querySelector("select[data-addon-select]");
      const check = card.querySelector("input[data-addon-variant-id]");

      let cents = 0;

      if (select) {
        const opt = select.options[select.selectedIndex];
        cents = parseInt(opt?.dataset?.priceCents || "0", 10);
      } else if (check) {
        cents = parseInt(check.dataset.addonPriceCents || "0", 10);
      }

      priceEl.textContent = `+ ${formatMoneyFromCents(cents)}`;
    }

    function updatePlus() {
      if (!plusEl) return;
      const { cents } = getSelectedAddons();
      plusEl.textContent = `+ ${formatMoneyFromCents(cents)}`;
    }

    // Add-on "What’s this?" pills
    cards.forEach((card) => {
      const btn = card.querySelector("[data-cjm-addon-whats-this]");
      if (!btn) return;

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
          const cfg = JSON.parse(btn.dataset.addonWtJson || "{}");
          const topic = (cfg.topics && (cfg.topics.about || cfg.topics["about"])) || null;
          if (!topic) return;

          openModalFromTopic(topic, btn.dataset.addonTitle);
        } catch (err) {
          console.warn("[CJM add-ons] bad whats_this_config JSON on add-on", err);
        }
      });
    });

    // Bind listeners per card
    cards.forEach((card) => {
      const check = card.querySelector("input[type='checkbox']");
      const select = card.querySelector("select[data-addon-select]");

      // Keep right-side card price correct on load + when variant changes
      updateCardPrice(card);
      if (select) {
        select.addEventListener("change", () => {
          updateCardPrice(card);
          updatePlus();
        });
      }

      if (check) check.addEventListener("change", updatePlus);
    });

    // initial totals
    updatePlus();

    // Find the closest product form in this product section
    const section = container.closest('section[id^="ProductInfo-"]') || root;
    const form = section.querySelector('form[action*="/cart/add"]');
    if (!form) {
      console.warn("[CJM add-ons] Could not find product form.");
      return;
    }

    // Avoid double-binding in theme editor
    if (form.dataset.cjmAddonsBound === "1") return;
    form.dataset.cjmAddonsBound = "1";

    async function addAllToCart() {
      const { items: addonItems } = getSelectedAddons();
      if (!addonItems.length) return false;

      const idEl = form.querySelector('input[name="id"]');
      const qtyEl = form.querySelector('input[name="quantity"]');

      const baseId = parseInt(idEl?.value || "0", 10);
      const qty = Math.max(1, parseInt(qtyEl?.value || "1", 10));

      if (!baseId) {
        console.warn("[CJM add-ons] Base variant id not found.");
        return false;
      }

      const items = [{ id: baseId, quantity: qty }, ...addonItems];

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error("Add to cart failed: " + t);
      }

      return true;
    }

    // ✅ Intercept submit in CAPTURE phase (beats Dawn)
    form.addEventListener(
      "submit",
      async (e) => {
        const { items: addonItems } = getSelectedAddons();
        if (!addonItems.length) return;

        console.log("[CJM add-ons] Intercepted add-to-cart with add-ons.");

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

        try {
          await addAllToCart();
          window.location.href = "/cart";
        } catch (err) {
          console.error("[CJM add-ons]", err);
          alert("Sorry — the add-on couldn't be added. The main item can still be added.");
        }
      },
      true
    );

    // ✅ Some Dawn builds trigger add via click handlers before submit.
    // Capture clicks within the buy-buttons area and preemptively submit ourselves.
    const buyButtons =
      section.querySelector(".product-form__buttons") ||
      section.querySelector("buy-buttons") ||
      section.querySelector("[data-buy-buttons]");

    if (buyButtons) {
      buyButtons.addEventListener(
        "click",
        async (e) => {
          const { items: addonItems } = getSelectedAddons();
          if (!addonItems.length) return;

          const clicked = e.target.closest("button, input, a");
          if (!clicked) return;

          console.log("[CJM add-ons] Intercepted buy-buttons click with add-ons.");

          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

          try {
            await addAllToCart();
            window.location.href = "/cart";
          } catch (err) {
            console.error("[CJM add-ons]", err);
            alert("Sorry — the add-on couldn't be added. The main item can still be added.");
          }
        },
        true
      );
    }
  }

  document.addEventListener("DOMContentLoaded", () => initAddons(document));
  document.addEventListener("shopify:section:load", (e) => initAddons(e.target));
})();

// CJM: move quantity inline to the left of ATC
(function () {
  function moveQtyInline() {
    const productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    if (!productInfo) return;

    const buttons = productInfo.querySelector(".product-form__buttons");
    if (!buttons) return;

    const atcWrapper =
      buttons.querySelector(".product-form__submit") ||
      buttons.querySelector('button[name="add"]')?.parentElement;

    const qtyBlock =
      productInfo.querySelector(".product-form__quantity") ||
      productInfo.querySelector(".product-form__input--quantity") ||
      productInfo.querySelector("quantity-input")?.closest(".product-form__input");

    if (!atcWrapper || !qtyBlock) return;

    // Prevent duplicate move
    if (buttons.contains(qtyBlock)) {
      buttons.classList.add("cjm-qty-inline");
      return;
    }

    buttons.insertBefore(qtyBlock, atcWrapper);
    buttons.classList.add("cjm-qty-inline");
  }

  document.addEventListener("DOMContentLoaded", moveQtyInline);
  document.addEventListener("shopify:section:load", moveQtyInline);
})();
